// @snippet
/**
 * @class Notification
 */
// import Picture from "../apps-react/global/Picture/Picture";
import Picture from './Picture';

const DEFAULT_THEME = 'default';
const DEFAULT_VARIANT = 'standard';
const ANIMATION_DURATION = 300;

const handle = document.querySelector('#gld-notification');
const handle_close = handle
  ? handle.querySelector('[data-notification-close]')
  : null;
const handle_icons = handle
  ? handle.querySelector('[data-notification-icons]')
  : null;
const handle_text = handle
  ? handle.querySelector('[data-notification-text]')
  : null;
const handle_icon_nodes = handle
  ? [...handle.querySelectorAll('[data-notification-icon]')]
  : [];
const handle_error_icon = handle
  ? handle.querySelector('[data-notification-icon="error"]')
  : null;
const notification_time_default = 5; //seconds
const notification_time_error = 15; //seconds
const activeStyleKeys = new Set();

let notificationTimeout = null;
let cleanupTimeout = null;

const notificationThemes = {
  [DEFAULT_THEME]: {
    modifier: null,
  },
  2: {
    modifier: '2',
  },
  product: {
    modifier: 'product',
    close: true,
    icon: false,
    notification_time: false,
  },
};

class Notification {
  static actionResolve = null;
  static assetsPromise = null;
  static markRequestId = 0;

  static themes = { ...notificationThemes };

  // constructor() {
  //     console.log('const Notification')
  //     // this.remove = this.remove.bind(this)
  // }

  static async mark(state, data, options = {}) {
    const requestId = ++Notification.markRequestId;
    await Notification.loadCSS();

    if (requestId !== Notification.markRequestId) {
      return null;
    }

    if (!handle) {
      return null;
    }

    const settings = options || {};
    const theme = Notification.getTheme(settings.theme);
    const variant = Notification.getVariant(settings.variant, theme);
    const notification_time = Notification.getOption(
      'notification_time',
      settings,
      theme,
      notification_time_default
    );
    const showIcon = Notification.getOption('icon', settings, theme, true);
    const showClose = Notification.getOption('close', settings, theme, false);
    const html = Array.isArray(data)
      ? Notification.renderNotificationList(data)
      : data || '';
    const actions = Array.isArray(settings.actions) ? settings.actions : [];
    const normalizedActions = Notification.normalizeActions(actions);
    const actionsHtml = Notification.renderActions(normalizedActions);
    const contentHtml = Notification.renderContent(
      html,
      actionsHtml,
      settings,
      theme
    );

    Notification.clearAutoRemoveTimer();
    Notification.clearCleanupTimer();
    Notification.clearActionResolver();
    Notification.resetHandle();
    Notification.applyVariant(variant);
    Notification.applyTheme(theme);
    Notification.applyStyles(settings.styles);

    handle.dataset.state = state;
    handle.dataset.notificationActive = 'true';
    handle.dataset.notificationAnimation = 'in';
    handle.style.removeProperty('display');

    if (handle_icons) {
      handle_icons.dataset.notificationVisible =
        showIcon === false ? 'false' : 'true';
    }
    handle_icon_nodes.forEach((icon) => {
      icon.dataset.notificationVisible =
        icon.dataset.notificationIcon === state ? 'true' : 'false';
    });

    if (handle_close) {
      handle_close.dataset.notificationVisible =
        showClose || notification_time === false ? 'true' : 'false';
    }

    if (handle_text) {
      handle_text.innerHTML = contentHtml;
    }

    if (normalizedActions.length && handle_text) {
      handle_text
        .querySelectorAll('[data-notification-action-index]')
        .forEach((btn) => {
          btn.addEventListener('click', async (e) => {
            const target = e.currentTarget;
            const actionIndex = Number(
              target.getAttribute('data-notification-action-index')
            );
            const action = normalizedActions[actionIndex];
            target.dataset.notificationLoading = 'true';
            if (typeof action?.function === 'function') {
              try {
                await action.function();
              } catch (err) {
                console.error('Notification action function error', err);
              }
            }
            const actionName =
              action?.name || target.getAttribute('data-notification-action');
            Notification.remove(actionName);
          });
        });
    }

    Notification.scheduleAutoRemove(state, notification_time);

    if (normalizedActions.length) {
      return new Promise((resolve) => {
        Notification.actionResolve = resolve;
      });
    }

    return null;
  }

  static registerTheme(name, config = {}) {
    const themeName = Notification.normalizeName(name);
    if (!themeName) {
      return null;
    }

    Notification.themes[themeName] = {
      ...config,
      modifier:
        config.modifier !== undefined || themeName === DEFAULT_THEME
          ? config.modifier
          : themeName,
    };

    return Notification.getTheme(themeName);
  }

  static getTheme(themeName = DEFAULT_THEME) {
    const name = Notification.normalizeName(themeName, DEFAULT_THEME);
    const config = Notification.themes[name] || {};

    return {
      ...config,
      name,
    };
  }

  static getVariant(variant, theme = {}) {
    const variantName =
      variant !== undefined
        ? variant
        : theme.variant !== undefined
          ? theme.variant
          : DEFAULT_VARIANT;

    return Notification.normalizeName(variantName, DEFAULT_VARIANT);
  }

  static getOption(key, options = {}, theme = {}, fallback = undefined) {
    if (options && options[key] !== undefined) {
      return options[key];
    }

    if (theme && theme[key] !== undefined) {
      return theme[key];
    }

    return fallback;
  }

  static normalizeName(value, fallback = '') {
    if (value === undefined || value === null || value === false) {
      return fallback;
    }

    const normalized = String(value).trim().toLowerCase();
    return normalized || fallback;
  }

  static getClassModifier(value) {
    if (value === undefined || value === null || value === false) {
      return '';
    }

    return String(value)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  static applyTheme(theme) {
    Notification.clearTheme();

    if (!theme) {
      return;
    }

    handle.dataset.notificationTheme = theme.name;
  }

  static clearTheme() {
    if (!handle) {
      return;
    }

    handle.removeAttribute('data-notification-theme');
  }

  static applyVariant(variant) {
    Notification.clearVariant();

    const modifier = Notification.getClassModifier(variant);
    if (!modifier) {
      return;
    }

    handle.dataset.notificationVariant = modifier;
  }

  static clearVariant() {
    if (!handle) {
      return;
    }
    handle.removeAttribute('data-notification-variant');
  }

  static applyStyles(styles = {}) {
    Notification.clearStyles();

    if (!styles) {
      return;
    }

    Object.keys(styles).forEach((key) => {
      if (styles[key] === undefined || styles[key] === null) {
        return;
      }

      if (key.indexOf('-') > -1) {
        handle.style.setProperty(key, styles[key]);
      } else {
        handle.style[key] = styles[key];
      }
      activeStyleKeys.add(key);
    });
  }

  static clearStyles() {
    if (!handle || !activeStyleKeys.size) {
      return;
    }

    activeStyleKeys.forEach((key) => {
      if (key.indexOf('-') > -1) {
        handle.style.removeProperty(key);
      } else {
        handle.style[key] = '';
      }
    });
    activeStyleKeys.clear();
  }

  static resetHandle() {
    if (!handle) {
      return;
    }

    Notification.clearTheme();
    Notification.clearVariant();
    Notification.clearStyles();

    handle.removeAttribute('data-state');
    handle.removeAttribute('data-notification-active');
    handle.removeAttribute('data-notification-animation');

    if (handle_icons) {
      handle_icons.removeAttribute('data-notification-visible');
    }
    handle_icon_nodes.forEach((icon) => {
      icon.removeAttribute('data-notification-visible');
    });

    if (handle_close) {
      handle_close.removeAttribute('data-notification-visible');
    }

    if (handle_text) {
      handle_text.innerHTML = '';
    }
  }

  static clearAutoRemoveTimer() {
    if (notificationTimeout) {
      clearTimeout(notificationTimeout);
      notificationTimeout = null;
    }
  }

  static clearCleanupTimer() {
    if (cleanupTimeout) {
      clearTimeout(cleanupTimeout);
      cleanupTimeout = null;
    }
  }

  static scheduleAutoRemove(state, notification_time) {
    Notification.clearAutoRemoveTimer();

    if (!notification_time) {
      return;
    }

    notificationTimeout = setTimeout(
      () => {
        notificationTimeout = null;
        Notification.remove('timeout');
      },
      (state === 'error' ? notification_time_error : notification_time) * 1000
    );
  }

  static renderContent(html, actionsHtml, options = {}, theme = {}) {
    const content = `${html}${actionsHtml}`;
    if (theme?.name !== 'product') {
      return content;
    }

    return Notification.renderProductContent(content, options?.product);
  }

  static renderProductContent(content, product = {}) {
    const imageHtml = Notification.renderProductImage(
      product?.image,
      product?.image_alt || product?.alt || product?.title
    );
    const title = product?.title || '';

    if (!imageHtml) {
      return content;
    }

    return `
    ${content}
      <div class="flex w-full items-start gap-4 border-t border-gld-border-subtle pt-3 max-[480px]:gap-3">
        ${imageHtml}
        <div class="flex min-w-0 flex-1 flex-col gap-4 text-xs">
          ${title}
        </div>
      </div>
    `;
  }

  static renderProductImage(image, fallbackAlt = '') {
    const imageData = Notification.normalizeProductImage(image, fallbackAlt);

    if (!imageData?.src) {
      return '';
    }

    const wrapper = document.createElement('div');
    wrapper.className =
      'aspect-square w-15 shrink-0 overflow-hidden rounded-lg border border-gld-border bg-[#f7f7f7] max-[480px]:w-18';

    const imageEl = Picture.render(
      {
        url: imageData.src,
        size_default: `320x320`,
        alt: imageData.alt || fallbackAlt || '',
      },
      {
        lazyload: false,
      }
    );
    imageEl.querySelector('img')?.classList.add('block', 'size-full', 'object-cover');
    wrapper.appendChild(imageEl);

    return wrapper.outerHTML;
  }

  static normalizeProductImage(image, fallbackAlt = '') {
    if (!image) {
      return null;
    }

    if (typeof image === 'string') {
      return {
        src: image.trim(),
        alt: fallbackAlt || '',
      };
    }

    return {
      src: String(image.url || image.src || '').trim(),
      alt: image.alt || fallbackAlt || '',
    };
  }

  static renderNotificationList(data = []) {
    if (!Array.isArray(data) || !data.length) {
      return '';
    }

    const list = data
      .filter((item) => item !== undefined && item !== null && item !== '')
      .map(
        (item) =>
          `<li class="flex flex-col gap-1.5 even:opacity-80 [&>*]:m-0 group-data-[notification-theme=product]/notification:gap-2 group-data-[notification-theme=product]/notification:even:opacity-100">${Notification.renderNotificationListItem(item)}</li>`
      )
      .join('');

    return list
      ? `<ul class="m-0 flex w-full list-none flex-col gap-4 p-0 group-data-[notification-theme=product]/notification:gap-3">${list}</ul>`
      : '';
  }

  static renderNotificationListItem(item) {
    if (typeof item === 'string') {
      return item;
    }

    const wrapper = document.createElement('div');

    const title = item?.title || '';
    if (title) {
      const titleEl = document.createElement('strong');
      titleEl.className =
        'm-0 group-data-[notification-theme=product]/notification:block group-data-[notification-theme=product]/notification:text-base group-data-[notification-theme=product]/notification:leading-tight group-data-[notification-theme=product]/notification:font-semibold group-data-[notification-theme=product]/notification:text-gld-ink';
      titleEl.textContent = title;
      wrapper.appendChild(titleEl);
    }

    const message = item?.message || item?.text || '';
    if (message) {
      const messageEl = document.createElement('p');
      messageEl.className =
        'm-0 group-data-[notification-theme=product]/notification:leading-[1.4] group-data-[notification-theme=product]/notification:text-[#4d4d4d]';
      messageEl.textContent = message;
      wrapper.appendChild(messageEl);
    }

    if (item?.details) {
      const detailsEl = document.createElement('p');
      detailsEl.className =
        'm-0 group-data-[notification-theme=product]/notification:leading-[1.4] group-data-[notification-theme=product]/notification:text-[#4d4d4d]';
      detailsEl.innerHTML = `<strong>Details:</strong> ${item.details}`;
      wrapper.appendChild(detailsEl);
    }

    return wrapper.innerHTML;
  }

  static remove(actionName = null) {
    Notification.resolveAction(actionName);

    if (!handle || handle.dataset.notificationActive !== 'true') {
      return;
    }

    Notification.clearAutoRemoveTimer();
    Notification.clearCleanupTimer();
    handle.dataset.notificationAnimation = 'out';

    cleanupTimeout = setTimeout(() => {
      cleanupTimeout = null;
      Notification.resetHandle();
    }, ANIMATION_DURATION);
  }

  static loadCSS() {
    if (!Notification.assetsPromise) {
      Notification.assetsPromise = import(
        /* webpackChunkName: "tailwind-notification" */ '../../tailwind/chunks/notification.css'
      );
    }
    return Notification.assetsPromise;
  }

  static clearNotification() {
    Notification.clearActionResolver();
    Notification.clearAutoRemoveTimer();
    Notification.clearCleanupTimer();
    Notification.resetHandle();
  }

  static resolveAction(actionName = null) {
    if (Notification.actionResolve) {
      Notification.actionResolve(actionName);
      Notification.actionResolve = null;
    }
  }

  static clearActionResolver() {
    if (Notification.actionResolve) {
      Notification.actionResolve(null);
      Notification.actionResolve = null;
    }
  }

  static renderActions(actions = []) {
    if (!actions.length) {
      return '';
    }

    const html = actions
      .filter((action) => action?.name && action?.label)
      .map((action, index) => {
        return `<button type="button" class="twc-btn twc-btn--gold twc-btn--small twc-btn--strong data-[notification-loading=true]:pointer-events-none data-[notification-loading=true]:opacity-60" data-notification-action="${action.name}" data-notification-action-index="${index}"><span class="twc-btn__text">${action.label}</span></button>`;
      })
      .join('');

    return html
      ? `<div class="flex w-full items-center gap-2.5 group-data-[notification-theme=product]/notification:justify-start" data-notification-actions>${html}</div>`
      : '';
  }

  static normalizeActions(actions = []) {
    return actions
      .map((action) => {
        const actionName = action?.name || action?.action;
        if (!actionName || !action?.label) {
          return null;
        }
        return {
          ...action,
          name: actionName,
        };
      })
      .filter(Boolean);
  }
}

const dismissNotification = (event) => {
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }
  Notification.remove('close');
};

if (handle_close) {
  handle_close.addEventListener('click', dismissNotification);
}

if (handle_error_icon) {
  handle_error_icon.addEventListener('click', dismissNotification);
  handle_error_icon.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      dismissNotification(event);
    }
  });
}

export default Notification;
